import { useState, useCallback } from 'react';
import { useTranslation } from '../i18n/useTranslation';
import { useTerminalStore } from '../store/useTerminalStore';

interface KillSwitchProps {
  onToggle?: (armed: boolean) => void;
  className?: string;
}

export function KillSwitch({ onToggle, className = '' }: KillSwitchProps) {
  const { t } = useTranslation();
  const armed = useTerminalStore((s) => s.killSwitchArmed);
  const [confirming, setConfirming] = useState(false);
  const [pendingAction, setPendingAction] = useState<boolean | null>(null);

  const handleClick = useCallback(() => {
    if (armed && !confirming) {
      setPendingAction(!armed);
      setConfirming(true);
      return;
    }
    const next = pendingAction ?? !armed;
    useTerminalStore.getState().setKillSwitch(next);
    setConfirming(false);
    setPendingAction(null);
    onToggle?.(next);
  }, [armed, confirming, pendingAction, onToggle]);

  const handleCancel = useCallback(() => {
    setConfirming(false);
    setPendingAction(null);
  }, []);

  return (
    <div className={`bg-card rounded border border-border flex flex-col ${className}`}>
      <div className="drag-handle h-6 flex items-center px-2 cursor-grab active:cursor-grabbing border-b border-border-subtle shrink-0">
        <span className="panel-header">{t('killswitch.header')}</span>
        <span className={`ml-auto status-pip ${armed ? 'status-pip--active' : 'status-pip--killed'}`} />
      </div>

      <div className="flex-1 flex flex-col items-center justify-center gap-2 p-3">
        <div className={`text-[11px] font-mono tracking-wider ${armed ? 'text-success' : 'text-danger'}`}>
          {armed ? t('killswitch.armed') : t('killswitch.killed')}
        </div>

        {confirming ? (
          <div className="flex gap-2 animate-fade-in">
            <button
              onClick={handleClick}
              className="px-3 py-1 text-[10px] font-mono bg-danger/20 text-danger
                         border border-danger/40 rounded-sm hover:bg-danger/30 transition-colors"
            >
              {t('killswitch.confirm')}
            </button>
            <button
              onClick={handleCancel}
              className="px-3 py-1 text-[10px] font-mono text-muted-foreground
                         border border-border rounded-sm hover:text-foreground transition-colors"
            >
              {t('killswitch.cancel')}
            </button>
          </div>
        ) : (
          <button
            onClick={handleClick}
            className={`px-4 py-1.5 text-[10px] font-mono rounded-sm border transition-colors ${
              armed
                ? 'text-danger border-danger/40 hover:bg-danger/10'
                : 'text-success border-success/40 hover:bg-success/10'
            }`}
          >
            {armed ? t('killswitch.kill_all') : t('killswitch.rearm')}
          </button>
        )}

        <span className="text-[8px] font-mono text-muted">
          {armed ? t('killswitch.active_combos') : t('killswitch.flat')}
        </span>
      </div>
    </div>
  );
}
